import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    collection, getDoc, doc, query, where,
    Timestamp, updateDoc, onSnapshot
} from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import {
    Box, Typography, CircularProgress, Divider,
    IconButton, Button, Switch,
} from '@mui/material';
import {
    ContentCut, CheckCircle, AccessTime,
    Logout, PersonOutline, ArrowBack,
} from '@mui/icons-material';
import BarberBottomNav from './BarberBottomNav';
import { colors, typography, radius, statusColors, switchSx, btnPrimary, btnSuccess, btnDanger, glassCard } from '../../theme';
import barberFallback from '../../images/Barber Icon (SVG).svg'
function formatTime(minutes) {
    if (!minutes && minutes !== 0) return '—';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h % 12 === 0 ? 12 : h % 12}:${m.toString().padStart(2,'0')} ${h < 12 ? 'AM' : 'PM'}`;
}

const StatCard = ({ icon, label, value, color }) => (
    <Box sx={{ bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.md, p: 1.5, flex: 1, ...glassCard }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>{icon}<Typography sx={{ fontSize: 11, color: colors.textFaint }}>{label}</Typography></Box>
        <Typography sx={{ fontFamily: typography.poppins, fontSize: 28, fontWeight: 700, color: color ?? colors.primary, lineHeight: 1 }}>{value}</Typography>
    </Box>
);

const BookingCard = ({ booking, onAction }) => {
    const s = statusColors[booking.status] ?? statusColors.pending;
    const isWalkin = booking.type === 'walkin';
    return (
        <Box sx={{ bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.md, p: 1.5 , ...glassCard}}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: colors.bg, border: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 ,...glassCard }}>
                    {isWalkin ? <PersonOutline sx={{ fontSize: 20, color: colors.textMuted }} /> : <ContentCut sx={{ fontSize: 18, color: colors.primary, }} />}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.2 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.textPrimary }} noWrap>{booking.customerName ?? 'Customer'}</Typography>
                        {isWalkin && booking.queueNumber && <Box sx={{ px: 0.8, py: 0.1, bgcolor: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 1 }}><Typography sx={{ fontSize: 9, color: colors.primary }}>#{booking.queueNumber}</Typography></Box>}
                        {isWalkin && <Box sx={{ px: 0.8, py: 0.1, bgcolor: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 1 }}><Typography sx={{ fontSize: 9, color: colors.textMuted }}>WALK-IN</Typography></Box>}
                        {booking.customerPhone && <Box sx={{ px: 0.8, py: 0.1, bgcolor: colors.successBg, border: `1px solid ${colors.successBorder}`, borderRadius: 1 }}><Typography sx={{ fontSize: 9, color: colors.success }}>📱 WA</Typography></Box>}
                    </Box>
                    <Typography sx={{ fontSize: 12, color: colors.textFaint }}>{booking.service ?? 'Service TBD'} · {formatTime(booking.slotMinutes)}</Typography>
                </Box>
                <Box sx={{ px: 1, py: 0.3, borderRadius: 1, bgcolor: s.bg, border: `1px solid ${s.border}`, flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 10, color: s.text, fontWeight: 600 }}>{booking.status?.toUpperCase()}</Typography>
                </Box>
            </Box>
            {(booking.status === 'waiting' || booking.status === 'pending') && (
                <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                    <Button fullWidth size="small" onClick={() => onAction(booking, 'serving')} sx={{ ...btnSuccess, fontSize: 12, fontWeight: 600, py: 0.8, borderRadius: 1.5 }}>
                        ✂️ Start Serving
                    </Button>
                    <Button size="small" onClick={() => onAction(booking, 'cancelled')} sx={{ ...btnDanger, fontSize: 12, px: 2, py: 0.8, borderRadius: 1.5 }}>
                        Cancel
                    </Button>
                </Box>
            )}
            {booking.status === 'serving' && (
                <Box sx={{ mt: 1.5 }}>
                    <Button fullWidth size="small" onClick={() => onAction(booking, 'completed')}
                        sx={{ bgcolor: colors.infoBg, border: `1px solid ${colors.infoBorder}`, color: colors.info, borderRadius: 1.5, textTransform: 'none', fontSize: 12, fontWeight: 600, py: 0.8, '&:hover': { bgcolor: colors.infoBorder } }}>
                        ✅ Mark as Done
                    </Button>
                </Box>
            )}
        </Box>
    );
};

const BarberDashboard = () => {
    const { shopId, barberId } = useParams();
    const navigate = useNavigate();

    const [barber,    setBarber]    = useState(null);
    const [shop,      setShop]      = useState(null);
    const [bookings,  setBookings]  = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [authReady, setAuthReady] = useState(false);
    const [innerTab,  setInnerTab]  = useState('queue');
    const [available, setAvailable] = useState(true);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (user) => { if (!user) navigate('/signin'); setAuthReady(true); });
        return () => unsub();
    }, [navigate]);

    useEffect(() => {
        if (!authReady) return;
        const load = async () => {
            try {
                const [barberSnap, shopSnap] = await Promise.all([
                    getDoc(doc(db, 'shops', shopId, 'barbers', barberId)),
                    getDoc(doc(db, 'shops', shopId)),
                ]);
                if (barberSnap.exists()) { const d = { id: barberSnap.id, ...barberSnap.data() }; setBarber(d); setAvailable(d.isAvailable !== false); }
                if (shopSnap.exists())   setShop({ id: shopSnap.id, ...shopSnap.data() });
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        };
        load();
    }, [authReady, shopId, barberId]);

    useEffect(() => {
        if (!authReady) return;
        const today = new Date(); today.setHours(0,0,0,0);
        const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
        const q = query(collection(db, 'bookings'), where('barberId', '==', barberId), where('scheduledAt', '>=', Timestamp.fromDate(today)), where('scheduledAt', '<', Timestamp.fromDate(tomorrow)));
        const unsub = onSnapshot(q, (snap) => {
            setBookings(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.slotMinutes ?? 0) - (b.slotMinutes ?? 0)));
        });
        return () => unsub();
    }, [authReady, barberId]);

    const handleToggleAvailable = async () => {
        const newVal = !available;
        setAvailable(newVal);
        try { await updateDoc(doc(db, 'shops', shopId, 'barbers', barberId), { isAvailable: newVal }); }
        catch (e) { console.error(e); setAvailable(!newVal); }
    };

    const handleAction = async (booking, newStatus) => {
        try { await updateDoc(doc(db, 'bookings', booking.id), { status: newStatus }); }
        catch (e) { console.error(e); }
    };

    const walkins      = bookings.filter(b => b.type === 'walkin');
    const appointments = bookings.filter(b => b.type === 'appointment');
    const serving      = bookings.find(b => b.status === 'serving');
    const waiting      = bookings.filter(b => b.status === 'waiting' || b.status === 'pending').length;
    const completed    = bookings.filter(b => b.status === 'completed').length;

    const queue = [...walkins, ...appointments]
        .filter(b => ['waiting','serving','pending'].includes(b.status))
        .sort((a, b) => {
            if (a.status === 'serving') return -1;
            if (b.status === 'serving') return 1;
            return (a.queueNumber ?? a.slotMinutes ?? 0) - (b.queueNumber ?? b.slotMinutes ?? 0);
        });

    const servingNow  = queue.find(b => b.status === 'serving');
    const next        = queue.find(b => ['waiting','pending'].includes(b.status));
    const waitingList = queue.filter(b => ['waiting','pending'].includes(b.status));

    const INNER = [
        { key: 'queue', label: `Queue (${walkins.filter(b => ['waiting','serving','pending'].includes(b.status)).length})` },
        { key: 'appts', label: `Appts (${appointments.length})` },
        { key: 'all',   label: `All (${bookings.length})` },
    ];

    if (!authReady || loading) return (
        <Box sx={{ minHeight: '100vh', bgcolor: colors.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress sx={{ color: colors.primary }} />
        </Box>
    );

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: colors.bg, color: colors.textPrimary, pb: 12 }}>

            {/* Top Bar */}
            <Box sx={{ position: 'sticky', top: 0, zIndex: 100, bgcolor: colors.bgTopBar, borderBottom: `1px solid ${colors.borderLight}`, px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <IconButton size="small" onClick={() => navigate(-1)} sx={{ color: colors.textMuted, p: 0.5 }}>
                    <ArrowBack fontSize="small" />
                </IconButton>
                <Box component="img" src={barber?.image || barberFallback} alt={barber?.name}
                    sx={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${colors.border}`, flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontFamily: typography.poppins, fontSize: 15, fontWeight: 700, color: colors.textPrimary, lineHeight: 1.1 }} noWrap>
                        {barber?.name ?? 'Barber'}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: colors.textFaint }}>{shop?.shopName}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: 11, color: available ? colors.success : colors.error }}>
                        {available ? 'Available' : 'Off'}
                    </Typography>
                    <Switch checked={available} onChange={handleToggleAvailable} size="small" sx={switchSx} />
                </Box>
                <IconButton size="small" onClick={() => signOut(auth).then(() => navigate('/signin'))} sx={{ color: colors.error, p: 0.5 }}>
                    <Logout fontSize="small" />
                </IconButton>
            </Box>

            {/* Stats */}
            <Box sx={{ px: 2, mt: 2, display: 'flex', gap: 1.5 }}>
                <StatCard icon={<AccessTime sx={{ fontSize: 13, color: colors.primary }} />}  label="Waiting"    value={waiting}         color={colors.primary} />
                <StatCard icon={<ContentCut sx={{ fontSize: 13, color: colors.info }} />}  label="Serving"    value={serving ? 1 : 0} color={colors.info} />
                <StatCard icon={<CheckCircle sx={{ fontSize: 13, color: colors.success }} />}    label="Done today" value={completed}        color={colors.success} />
            </Box>

            {/* Inner tabs */}
            <Box sx={{ display: 'flex', px: 2, mt: 2, gap: 1 }}>
                {INNER.map(tab => (
                    <Box key={tab.key} onClick={() => setInnerTab(tab.key)} sx={{
                        flex: 1, textAlign: 'center', py: 0.7, borderRadius: radius.md, cursor: 'pointer',
                        bgcolor: innerTab === tab.key ? colors.primary : colors.bg,
                        border: `1px solid ${innerTab === tab.key ? colors.primary : colors.primary}`,
                        transition: 'all .2s',
                    }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: innerTab === tab.key ? colors.bg : colors.textPrimary }}>
                            {tab.label}
                        </Typography>
                    </Box>
                ))}
            </Box>

            <Divider sx={{ borderColor: colors.primary, mx: 2, mt: 2 }} />

            {/* Queue tab */}
            {innerTab === 'queue' && (
                <Box sx={{ px: 2, mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography sx={{ fontFamily: typography.poppins, fontSize: 14, fontWeight: 600, color: colors.primary }}>Now Serving</Typography>

                    {servingNow ? (
                        <Box sx={{ bgcolor: colors.successBg, border: `2px solid ${colors.successBorder}`, borderRadius: radius.lg, p: 2 ,...glassCard}}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                <Box sx={{ width: 52, height: 52, borderRadius: '50%', bgcolor: colors.bg, border: `2px solid ${colors.successBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <Typography sx={{ fontSize: 22 }}>{servingNow.type === 'walkin' ? '🚶' : '📅'}</Typography>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontFamily: typography.poppins, fontSize: 16, fontWeight: 700, color: colors.textPrimary }}>{servingNow.customerName ?? 'Customer'}</Typography>
                                    <Typography sx={{ fontSize: 12, color: colors.success }}>{servingNow.service ?? 'Service TBD'} · {formatTime(servingNow.slotMinutes)}</Typography>
                                    {servingNow.queueNumber && <Typography sx={{ fontSize: 11, color: colors.textFaint, mt: 0.2 }}>Queue #{servingNow.queueNumber}</Typography>}
                                </Box>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colors.success, boxShadow: `0 0 8px ${colors.success}` }} />
                            </Box>
                            <Button fullWidth onClick={() => handleAction(servingNow, 'completed')}
                                sx={{ bgcolor: colors.success, color: colors.bg, fontWeight: 700, textTransform: 'none', borderRadius: radius.md, py: 1, fontSize: 13, '&:hover': { bgcolor: colors.success } }}>
                                ✅ Done — Call Next
                            </Button>
                        </Box>
                    ) : (
                        <Box sx={{ bgcolor: colors.bgCard, border: `1px dashed ${colors.border}`, borderRadius: radius.lg, p: 3, textAlign: 'center' ,...glassCard}}>
                            <Typography sx={{ fontSize: 24, mb: 1 }}>✂️</Typography>
                            <Typography sx={{ fontSize: 13, color: colors.textFaint }}>No one being served</Typography>
                            {next && (
                                <Button onClick={() => handleAction(next, 'serving')}
                                    sx={{ mt: 1.5, ...btnPrimary, px: 3, py: 0.8, fontSize: 13 }}>
                                    ▶ Call #{next.queueNumber ?? '—'} {next.customerName}
                                </Button>
                            )}
                        </Box>
                    )}

                    {next && (
                        <Box>
                            <Typography sx={{ fontFamily: typography.poppins, fontSize: 14, fontWeight: 600, color: colors.primary, mb: 1 }}>Up Next</Typography>
                            <Box sx={{ bgcolor: colors.bgCard, border: `1px solid ${colors.primaryBorder}`, borderRadius: radius.md, p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5,...glassCard }}>
                                <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: colors.bg, border: `1px solid ${colors.primaryBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <Typography sx={{ fontSize: 18 }}>{next.type === 'walkin' ? '🚶' : '📅'}</Typography>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.textPrimary }}>{next.customerName ?? 'Customer'}</Typography>
                                    <Typography sx={{ fontSize: 12, color: colors.textMuted }}>{next.service ?? 'Service TBD'} · {formatTime(next.slotMinutes)}{next.queueNumber ? ` · #${next.queueNumber}` : ''}</Typography>
                                    {next.customerPhone && <Typography sx={{ fontSize: 11, color: colors.success, mt: 0.2 }}>📱 Will be auto-notified via WhatsApp</Typography>}
                                </Box>
                                <Box sx={{ px: 1, py: 0.3, borderRadius: 1, bgcolor: colors.warningBg, border: `1px solid ${colors.primaryBorder}` }}>
                                    <Typography sx={{ fontSize: 10, color: colors.primary, fontWeight: 600 }}>NEXT</Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {waitingList.length > (next ? 1 : 0) && (
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography sx={{ fontFamily: typography.poppins, fontSize: 14, fontWeight: 600, color: colors.primary }}>Waiting</Typography>
                                <Typography sx={{ fontSize: 11, color: colors.textFaint }}>{waitingList.length - (next ? 1 : 0)} more</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {waitingList.filter(b => b.id !== next?.id).map((b, i) => (
                                    <Box key={b.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.md, p: 1.2 ,...glassCard}}>
                                        <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: colors.bg, border: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 ,...glassCard}}>
                                            <Typography sx={{ fontSize: 11, color: colors.textPrimary, fontWeight: 600, }}>{i + 2}</Typography>
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography sx={{ fontSize: 13, color: colors.textPrimary }}>{b.customerName ?? 'Customer'}{b.queueNumber ? ` · #${b.queueNumber}` : ''}{b.customerPhone ? ' 📱' : ''}</Typography>
                                            <Typography sx={{ fontSize: 11, color: colors.textFaint }}>{b.service ?? 'Service TBD'} · {formatTime(b.slotMinutes)}</Typography>
                                        </Box>
                                        <Button size="small" onClick={() => handleAction(b, 'cancelled')}
                                            sx={{ color: colors.textFaintest, fontSize: 11, textTransform: 'none', minWidth: 0, px: 1, '&:hover': { color: colors.error } }}>
                                            ✕
                                        </Button>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {queue.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Typography sx={{ fontSize: 32 }}>✂️</Typography>
                            <Typography sx={{ fontSize: 14, color: colors.textFaint, mt: 1 }}>Queue is empty</Typography>
                            <Typography sx={{ fontSize: 12, color: colors.textFaintest, mt: 0.5 }}>Walk-ins will appear here automatically</Typography>
                        </Box>
                    )}
                </Box>
            )}

            {/* Appts tab */}
            {innerTab === 'appts' && (
                <Box sx={{ px: 2, mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography sx={{ fontFamily: typography.poppins, fontSize: 14, fontWeight: 600, color: colors.primary }}>Today's Appointments</Typography>
                    {appointments.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 5 }}>
                            <Typography sx={{ fontSize: 28 }}>📅</Typography>
                            <Typography sx={{ fontSize: 14, color: colors.textFaint, mt: 1 }}>No appointments today</Typography>
                        </Box>
                    ) : appointments.map(b => <BookingCard key={b.id} booking={b} onAction={handleAction} />)}
                </Box>
            )}

            {/* All tab */}
            {innerTab === 'all' && (
                <Box sx={{ px: 2, mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography sx={{ fontFamily: typography.poppins, fontSize: 14, fontWeight: 600, color: colors.primary }}>All Bookings Today</Typography>
                    {bookings.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 5 }}>
                            <Typography sx={{ fontSize: 28 }}>📋</Typography>
                            <Typography sx={{ fontSize: 14, color: colors.textFaint, mt: 1 }}>No bookings today</Typography>
                        </Box>
                    ) : bookings.map(b => <BookingCard key={b.id} booking={b} onAction={handleAction} />)}
                </Box>
            )}

            <BarberBottomNav shopId={shopId} barberId={barberId} />
        </Box>
    );
};

export default BarberDashboard;